import type { DocPage } from "../types";

export const page: DocPage = {
  slug: "getting-started",
  title: "Getting started with Mavori",
  navLabel: "Getting started",
  description:
    "Complete a first Mavori session: ask a focused question, inspect the evidence, and choose a safe next step.",
  section: "start",
  published: "2026-08-03",
  updated: "2026-08-03",
  targets: ["Mavori getting started", "first Mavori session", "how to start using Mavori"],
  related: ["agent", "markets", "strategies", "accounts"],
  blocks: [
    {
      type: "lead",
      text: "You can begin with research and strategy work before connecting any financial account. A useful first session starts with one focused question, checks the evidence behind the answer, and saves or continues only the work worth keeping.",
    },

    { type: "h2", text: "Start with a focused question" },
    {
      type: "p",
      text: "Open a new chat and name the company, market, or decision you are exploring. Add a time horizon and the evidence you care about. For example: **Compare AMD and NVDA on growth, margins, valuation, and the next reported catalysts. Show the source and date for each important figure.**",
    },
    {
      type: "p",
      text: "A narrow first request makes the result easier to verify. Follow up in the same conversation when you want to challenge an assumption, change the comparison, or ask the agent to explain a missing figure.",
    },

    { type: "h2", text: "Review the evidence" },
    {
      type: "p",
      text: "Read the answer first, then open its activity details and any attached panel. Check the source, as-of time, and coverage state for figures that affect your conclusion. **Partial**, **missing**, or **unavailable** means the answer should be narrowed or qualified, not treated as complete. [Data coverage and freshness](/docs/data) explains these states.",
    },

    { type: "h2", text: "Continue into a product flow" },
    {
      type: "table",
      caption: "Useful next steps after a first answer",
      headers: ["Goal", "Next step"],
      rows: [
        ["Explore one company", "Open its report in Markets and compare the relevant tabs"],
        ["Find a group of stocks", "Run a screen, then research the strongest matches"],
        [
          "Test a repeatable rule",
          "Create a Strategy and run a backtest before considering deployment",
        ],
        [
          "Use portfolio context",
          "Create a paper account or connect a supported brokerage account",
        ],
        ["Keep the result", "Save a report or working note in Files"],
      ],
    },

    { type: "h2", text: "Add account access when it helps" },
    {
      type: "p",
      text: "Portfolio access is optional for general research. When a question depends on holdings, cash, or exposure, select the relevant account for that conversation. Start with a paper account if you want to learn the trading and deployment flows without using real money. Review [Portfolio accounts](/docs/accounts) before adding a live connection.",
    },

    { type: "h2", text: "Keep financial actions deliberate" },
    {
      type: "p",
      text: "Creating research, files, or a strategy does not place a trade. When the agent prepares an order, review the account, symbol, side, quantity, order type, estimated value, and applicable controls on the proposal. Placement happens only through the approval flow described in [Risk controls, approvals, and orders](/docs/trading).",
    },
  ],
};
